import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState } from "react";
import { RiEyeLine, RiEyeOffLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "utils/services/api";
import * as Yup from "yup";

const ResetPasswordForm = () => {
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState({ password: false, confirm: false });
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const toggleVisibility = (field) =>
    setVisible((prev) => ({ ...prev, [field]: !prev[field] }));

  const initialValues = { password: "", confirmPassword: "" };

  const validationSchema = Yup.object({
    password: Yup.string()
      .trim()
      .required("Password is required")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,14}$/,
        "Password should be 8-14 characters with at least one lowercase, one uppercase, one number, and one special character from @$!%*?&#."
      ),
    confirmPassword: Yup.string()
      .trim()
      .required("Confirm Password is required")
      .oneOf(
        [Yup.ref("password"), null],
        "Password and Confirm Password must match."
      ),
  });

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        `/auth/reset-password?token=${token}&password=${values.password}`,
        { password: values.password }
      );
      toast.success(response?.data?.message);
      resetForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred.");
      resetForm();
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const renderField = (id, name, label, placeholder, visibilityKey) => (
    <div className="mb-3">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label} <span className="text-red-700">*</span>
      </label>
      <div className="relative">
        <Field
          type={visible[visibilityKey] ? "text" : "password"}
          id={id}
          name={name}
          autoComplete={id}
          placeholder={placeholder}
          className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-gray-700 dark:border-white/10 dark:text-white"
        />
        <span
          className="absolute right-3 top-4 cursor-pointer"
          onClick={() => toggleVisibility(visibilityKey)}
        >
          {visible[visibilityKey] ? <RiEyeLine /> : <RiEyeOffLine />}
        </span>
      </div>
      <ErrorMessage
        name={name}
        component="div"
        className="mt-1 text-sm text-red-500"
      />
    </div>
  );

  return (
    <div className="mb-16 mt-16 flex h-full w-full items-center justify-center px-2">
      <div className="mt-[10vh] w-full max-w-[500px] flex-col items-center md:pl-4">
        <div className="mx-auto w-full max-w-[400px] rounded-xl bg-white p-6 shadow-lg dark:bg-navy-900">
          <h4 className="mb-2.5 text-center text-4xl font-bold text-navy-700">
            Reset Password
          </h4>
          <p className="mb-9 text-center text-base text-gray-600">
            Please enter your new password securely.
          </p>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              {renderField(
                "password",
                "password",
                "Password",
                "Enter Password",
                "password"
              )}
              {renderField(
                "confirmPassword",
                "confirmPassword",
                "Confirm Password",
                "Enter Confirm Password",
                "confirm"
              )}

              <button
                type="submit"
                className="mt-2 w-full rounded-xl bg-blueSecondary py-3 text-base font-medium text-white transition duration-200 active:bg-[#14AADD] dark:bg-brand-400 dark:hover:bg-brand-300"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </Form>
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordForm;